/**
 * Occurrence identity.
 *
 * Occurrences are computed from a series, never stored. They are addressed by a
 * stable key of `${seriesId}:${YYYY-MM-DD}`, where the date is the occurrence's
 * ORIGINAL local date in the location's timezone.
 *
 * Using the original date (rather than a rescheduled one) is what lets an
 * override survive a later reschedule: the key never moves once it exists, so a
 * class can be moved repeatedly without losing its identity. This also maps
 * directly onto iCalendar's RECURRENCE-ID.
 */
export type OccurrenceKey = string & { readonly __brand: 'OccurrenceKey' };

const KEY_PATTERN = /^(?<seriesId>[^:]+):(?<date>\d{4}-\d{2}-\d{2})$/u;

export function makeOccurrenceKey(seriesId: string, isoDate: string): OccurrenceKey {
  if (seriesId.length === 0) {
    throw new Error('seriesId must not be empty');
  }
  if (seriesId.includes(':')) {
    throw new Error(`seriesId must not contain ':' (got "${seriesId}")`);
  }
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(isoDate)) {
    throw new Error(`date must be YYYY-MM-DD (got "${isoDate}")`);
  }
  return `${seriesId}:${isoDate}` as OccurrenceKey;
}

export function parseOccurrenceKey(key: string): { seriesId: string; isoDate: string } {
  const match = KEY_PATTERN.exec(key);
  if (!match?.groups) {
    throw new Error(`Malformed occurrence key: "${key}"`);
  }
  return { seriesId: match.groups.seriesId as string, isoDate: match.groups.date as string };
}
